import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import axios from "axios";
import { ChevronLeft, ChevronRight, Users } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CalendarCapacity = () => {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [capacityData, setCapacityData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(null);
  const [dayReservations, setDayReservations] = useState([]);

  useEffect(() => {
    fetchCapacityData();
  }, [currentMonth]);

  const fetchCapacityData = async () => {
    try {
      setLoading(true);
      const year = currentMonth.getFullYear();
      const month = currentMonth.getMonth();
      
      const startDate = new Date(year, month, 1).toISOString().split('T')[0];
      const endDate = new Date(year, month + 1, 0).toISOString().split('T')[0];
      
      const response = await axios.get(`${API}/calendar/capacity?start_date=${startDate}&end_date=${endDate}`);
      setCapacityData(response.data);
    } catch (error) {
      console.error("Error fetching capacity data:", error);
      toast.error("Error loading calendar");
    } finally {
      setLoading(false);
    }
  };

  const fetchDayReservations = async (dateStr) => {
    try {
      const response = await axios.get(`${API}/reservations/detailed?date=${dateStr}`);
      // Sort by time
      const sortedReservations = response.data.sort((a, b) => {
        return a.time.localeCompare(b.time);
      });
      setDayReservations(sortedReservations);
      setSelectedDate(dateStr);
    } catch (error) {
      console.error("Error fetching day reservations:", error);
    }
  };

  const prevMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1));
    setSelectedDate(null);
    setDayReservations([]);
  };

  const nextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));
    setSelectedDate(null);
    setDayReservations([]);
  };

  const getCapacityForDate = (dateStr) => {
    return capacityData.find(d => d.date === dateStr) || { 
      total_guests: 0, 
      total_reservations: 0, 
      capacity_percentage: 0, 
      status: 'available' 
    };
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'full': return 'bg-red-500/30 border-red-500/50 text-red-300';
      case 'busy': return 'bg-orange-500/30 border-orange-500/50 text-orange-300';
      default: return 'bg-green-500/20 border-green-500/30 text-green-300';
    }
  };

  const monthNames = [
    "Januari", "Februari", "Maart", "April", "Mei", "Juni",
    "Juli", "Augustus", "September", "Oktober", "November", "December"
  ];

  const dayNames = ["Zo", "Ma", "Di", "Wo", "Do", "Vr", "Za"];

  // Generate calendar days
  const year = currentMonth.getFullYear();
  const month = currentMonth.getMonth();
  const firstDayOfMonth = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  
  const calendarDays = [];
  // Add empty cells for days before the first day of the month
  for (let i = 0; i < firstDayOfMonth; i++) {
    calendarDays.push(null);
  }
  // Add days of the month
  for (let day = 1; day <= daysInMonth; day++) {
    calendarDays.push(day);
  }

  return (
    <div className="flex-1 flex flex-col overflow-hidden" data-testid="calendar-capacity-view">
      <div className="p-6 border-b border-white/10">
        <h2 className="text-2xl font-serif text-gray-300" data-testid="calendar-title">Kalender Overzicht</h2>
        <p className="text-sm text-gray-400">Capaciteit en bezetting per dag</p>
      </div>

      <div className="flex-1 overflow-auto p-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Calendar */}
          <div className="lg:col-span-2 bg-[#1A1A1A] border border-white/10 rounded-lg p-4">
            {/* Month Navigation */}
            <div className="flex items-center justify-between mb-4">
              <Button variant="outline" size="sm" onClick={prevMonth} className="border-white/20">
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <h3 className="text-lg font-serif text-gray-200">
                {monthNames[month]} {year}
              </h3>
              <Button variant="outline" size="sm" onClick={nextMonth} className="border-white/20">
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>

            {/* Day Headers */}
            <div className="grid grid-cols-7 gap-1 mb-2">
              {dayNames.map(day => (
                <div key={day} className="text-center text-xs font-semibold text-gray-500 py-2">
                  {day}
                </div>
              ))}
            </div>

            {/* Calendar Grid */}
            {loading ? (
              <div className="text-center py-12 text-gray-400">Laden...</div>
            ) : (
              <div className="grid grid-cols-7 gap-1">
                {calendarDays.map((day, index) => {
                  if (day === null) {
                    return <div key={`empty-${index}`} className="h-20" />;
                  }
                  
                  const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
                  const capacity = getCapacityForDate(dateStr);
                  const isToday = new Date().toISOString().split('T')[0] === dateStr;
                  const isSelected = selectedDate === dateStr;
                  
                  return (
                    <div
                      key={dateStr}
                      onClick={() => fetchDayReservations(dateStr)}
                      className={`h-20 p-1 border rounded-lg cursor-pointer transition-all hover:border-brand-gold/50 ${
                        isSelected ? 'border-brand-gold ring-1 ring-brand-gold' : 'border-white/10'
                      } ${getStatusColor(capacity.status)}`}
                      data-testid={`calendar-day-${dateStr}`}
                    >
                      <div className="flex flex-col h-full">
                        <span className={`text-sm font-semibold ${isToday ? 'text-brand-gold' : ''}`}>
                          {day}
                        </span>
                        {capacity.total_reservations > 0 && (
                          <div className="mt-auto text-xs">
                            <p className="font-semibold">{capacity.total_reservations} res.</p>
                            <p className="text-gray-400">{capacity.total_guests} gasten</p>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Legend */}
            <div className="flex items-center gap-4 mt-4 pt-4 border-t border-white/10">
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded bg-green-500/20 border border-green-500/30" />
                <span className="text-xs text-gray-400">Beschikbaar</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded bg-orange-500/30 border border-orange-500/50" />
                <span className="text-xs text-gray-400">Druk (60-90%)</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded bg-red-500/30 border border-red-500/50" />
                <span className="text-xs text-gray-400">Vol (90%+)</span>
              </div>
            </div>
          </div>

          {/* Day Details */}
          <div className="bg-[#1A1A1A] border border-white/10 rounded-lg p-4">
            <h3 className="text-lg font-serif text-gray-300 mb-4">
              {selectedDate ? (
                <>Reserveringen {new Date(selectedDate).toLocaleDateString('nl-NL', { weekday: 'long', day: 'numeric', month: 'long' })}</>
              ) : (
                'Selecteer een dag'
              )}
            </h3>

            {!selectedDate ? (
              <p className="text-gray-500 text-sm">Klik op een dag om de reserveringen te bekijken</p>
            ) : dayReservations.length === 0 ? (
              <p className="text-gray-500 text-sm">Geen reserveringen voor deze dag</p>
            ) : (
              <div className="space-y-3 max-h-[500px] overflow-y-auto">
                {dayReservations.map((res, index) => (
                  <div 
                    key={res.id} 
                    className="p-3 bg-[#222222] rounded-lg border border-white/5"
                    data-testid={`day-reservation-${index}`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <span className="text-brand-gold font-semibold">{res.time}</span>
                        <span className={`px-1.5 py-0.5 text-[10px] rounded ${
                          res.source === 'website' 
                            ? 'bg-cyan-500/20 text-cyan-400' 
                            : 'bg-amber-500/20 text-amber-400'
                        }`}>
                          {res.source === 'website' ? 'Online' : 'Handmatig'}
                        </span>
                        {res.source !== 'website' && res.taken_by && (
                          <span className="px-1.5 py-0.5 text-[10px] bg-gray-500/20 text-gray-400 rounded">
                            {res.taken_by}
                          </span>
                        )}
                      </div>
                      <span className="text-xs text-gray-400 flex items-center gap-1">
                        <Users className="w-3 h-3" />
                        {res.party_size}
                      </span>
                    </div>
                    <p className="text-sm text-gray-300">{res.guest_name || res.guest?.name || 'Onbekend'}</p>
                    {res.table && (
                      <p className="text-xs text-blue-400 mt-1">Tafel {res.table.number}</p>
                    )}
                    {res.special_requests && (
                      <p className="text-xs text-gray-500 mt-1">📝 {res.special_requests}</p>
                    )}
                  </div>
                ))}
              </div>
            )}

            {selectedDate && dayReservations.length > 0 && (
              <div className="mt-4 pt-4 border-t border-white/10">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-400">Totaal reserveringen:</span>
                  <span className="text-gray-200 font-semibold">{dayReservations.length}</span>
                </div>
                <div className="flex justify-between text-sm mt-1">
                  <span className="text-gray-400">Totaal gasten:</span>
                  <span className="text-gray-200 font-semibold">
                    {dayReservations.reduce((sum, r) => sum + (r.party_size || 0), 0)}
                  </span>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CalendarCapacity;
